// @react
import React, { useEffect, useRef } from 'react'
// @typings
import { CreateAccountFormValues } from 'modules/common/forms/types/createAccount'
import {
  GetConstantsQuery,
  GetConstantsQueryVariables,
  GetUserQuery,
} from 'lib/graphql/graphqlTypes'
// @graphQL
import { useQuery } from '@apollo/client'
import { GET_CONSTANTS } from 'graphql/queries/constants.query'
// @libraries
import {
  Control,
  Controller,
  FieldErrors,
  UseFormMethods,
} from 'react-hook-form'
import { Dictionary } from 'constants/interface'
// @common
import QueryError from 'modules/common/components/Errors/QueryError'
// @components
import Grid from '@bit/atd.web.grid'
import Banner from '@bit/atd.web.banner'
import Select from '@bit/atd.web.select'
import strings from 'lib/constants/strings'

type PropsType = {
  control: Control<CreateAccountFormValues>
  formMethods: UseFormMethods<CreateAccountFormValues>
  errors: FieldErrors
  userData?: GetUserQuery
}

const ProfessionalFields = ({
  control,
  errors,
  userData,
  formMethods,
}: PropsType) => {
  /******************************************
   * REFS
   ******************************************/

  const roleRef = useRef<HTMLInputElement | null>(null)
  const industryRef = useRef<HTMLInputElement | null>(null)

  /******************************************
   * GQL HOOKS
   ******************************************/

  const {
    loading: isConstantsLoading,
    error: isConstantsError,
    data: constantsData,
  } = useQuery<GetConstantsQuery, GetConstantsQueryVariables>(GET_CONSTANTS)

  /******************************************
   * HOOKS
   ******************************************/

  useEffect(() => {
    if (userData?.user?.id) {
      const userValues: Dictionary<string | null | undefined> = {
        industry: userData?.user?.job.jobIndustry,
        role: userData?.user?.job.jobFunctionCode,
      }
      Object.keys(userValues).map((item) => {
        // @ts-ignore
        formMethods.setValue(`${item}`, userValues[item])
      })
    }
  }, [isConstantsLoading, userData?.user?.id])

  if (isConstantsError || (!constantsData && !isConstantsLoading)) {
    return <QueryError />
  }

  /******************************************
   * FUNCTIONS
   ******************************************/

  const createDataLoadedField = (
    constantsData: GetConstantsQuery,
    type: 'role' | 'industry',
  ) => {
    const { industryCodes, jobRoleCodes } = constantsData.constants
    const roleOptions = jobRoleCodes.map((item: any) => {
      return {
        value: item.key,
        label: item.value,
      }
    })
    const industryOptions = industryCodes.map((item: any) => {
      return {
        value: item.key,
        label: item.value,
      }
    })

    /******************************************
     * RENDER
     ******************************************/

    return (
      <>
        {type === 'role' && (
          <Controller
            control={control}
            onFocus={() => roleRef?.current?.focus()}
            name="role"
            render={({ onChange, onBlur, value }) => (
              <Select
                native
                name="role"
                label="Role"
                placeholder={strings.PROFILE.ACCESS.ROLE}
                required
                onBlur={onBlur}
                onChange={onChange}
                value={value}
                inputRef={roleRef}
                options={roleOptions}
                error={!!errors?.role}
                errorText={errors?.role?.message}
              />
            )}
          />
        )}
        {type === 'industry' && (
          <Controller
            control={control}
            name="industry"
            onFocus={() => industryRef?.current?.focus()}
            render={({ onChange, onBlur, value }) => (
              <Select
                native
                name="industry"
                label="Industry"
                placeholder={strings.PROFILE.ACCESS.INDUSTRY}
                required
                onBlur={onBlur}
                onChange={onChange}
                value={value}
                inputRef={industryRef}
                options={industryOptions}
                error={!!errors?.industry}
                errorText={errors?.industry?.message}
              />
            )}
          />
        )}
      </>
    )
  }

  return (
    <>
      <Grid container spacing={4}>
        {/* Banner */}
        <Grid xs={12} item>
          <Banner icon="alert">
            We use this information to serve up content that's most relevant to
            you.
          </Banner>
        </Grid>
        {/* Role and Industry */}
        <Grid xs={12} sm={6} item>
          {(isConstantsError || !constantsData) && <p>Error</p>}
          {constantsData && createDataLoadedField(constantsData, 'industry')}
        </Grid>
        <Grid xs={12} sm={6} item>
          {(isConstantsError || !constantsData) && <p>Error</p>}
          {constantsData && createDataLoadedField(constantsData, 'role')}
        </Grid>
      </Grid>
    </>
  )
}

export default ProfessionalFields
